import React from "react";
import { withRouter } from "react-router-dom";
import SelectSearch from "react-select-search/dist/cjs";
import moment from "moment";
import Swal from "sweetalert2";
import { groupBy, forEach } from "lodash";

import {
	getMerchants,
	getTotalCommissions,
	getSettlements,
	getAccessControl,
	getReportToken,
	getReport,
} from "../../libs/functions";
import Util from "../../libs/util";

import $ from "jquery";

import Container from "../../components/Container";
import StatisticsGrid from "./components/StatisticsGrid";
import DateRange from "./components/DateRange";
import withSSR from "../../components/withSSR";
import DataTable from "../../components/DataTable";

import { reportAxios } from "../../libs/axiosInstances";

class Commissions extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			merchants: [],
			viewingMerchant: null,
			customList: [],
			mode: "Active",
			isLoading: true,
			master: false,
			selectedMerchant: null,
			commissionList: null,
			commissionListTotal: 0,
			commissionListTransactions: 0,
			commissionSummary: null,
			viewingRange: {
				start: moment().startOf("month"),
				end: moment(),
				maxDate: moment(),
			},
			reportConfig: null,
			queryCounter: 0,
		};

		this.commissionsRef = React.createRef();
	}

	componentDidMount = async () => {
		this.setState({
			master: (await getAccessControl()) === "master",
			reportConfig: await getReportToken(),
		});
		this.retrieveMerchants();
	};

	retrieveMerchants = async () => {
		this.setState({ isLoading: true, merchants: [] });

		const data = await this.props.fetchData(
			this.container.loader,
			getMerchants,
			{
				isArchive: this.state.mode !== "Active",
			}
		);

		let merchants = data.map((merchant) => {
			return { name: merchant.name, value: merchant.id };
		});
		merchants.unshift({ name: "All", value: "all" });

		this.setState({ merchants });
	};

	selectMerchant = (value) => {
		this.setState({ selectedMerchant: value });
	};

	dateChange = (dates) => {
		const [start, end] = dates;
		const current = { ...this.state.viewingRange };

		if (start) current.start = moment(start);
		else current.start = null;
		if (end) current.end = moment(end);
		else current.end = null;

		if (current.start) {
			const newMaxDate = current.start.clone().add(1, "months");
			if (newMaxDate.isBefore(new Date())) current.maxDate = newMaxDate;
		}

		this.setState({ viewingRange: current });
	};

	retrieveTotalCommissions = async (payload) => {
		const {
			merchants,
			selectedMerchant,
			viewingRange: { start, end },
			master,
		} = payload;

		const { totalCommission, totalTransactions } = await getTotalCommissions({
			merchant:
				selectedMerchant === "all"
					? merchants.map((m) => m.value).filter((m) => m !== "all")
					: [selectedMerchant],
			startDate: start.startOf("day").toISOString(),
			endDate: end.endOf("day").toISOString(),
			isMaster: master,
		});
		this.setState({
			commissionListTotal: totalCommission,
			commissionListTransactions: totalTransactions,
		});
	};

	retrieveSettlements = async (payload) => {
		this.setState({ commissionSummary: null });
		const {
			merchants,
			selectedMerchant,
			viewingRange: { start, end },
		} = payload;

		const settlements = await getSettlements({
			merchant:
				selectedMerchant === "all"
					? merchants.map((m) => m.value).filter((m) => m !== "all")
					: [selectedMerchant],
			startDate: start.startOf("day").toISOString(),
			endDate: end.endOf("day").toISOString(),
		});

		this.setState({ commissionSummary: settlements });
	};

	executeCommissionQuery = async () => {
		this.setState({ queryCounter: this.state.queryCounter + 1 });
		if (this.commissionsRef && this.commissionsRef.current)
			this.commissionsRef.current.reloadAjax();
		const queryPromises = [
			this.retrieveTotalCommissions(this.state),
			this.retrieveSettlements(this.state),
		];
		await Promise.all(queryPromises);
	};

	search = async () => {
		if (this.state.selectedMerchant === null) return;
		Swal.fire({
			title: "Retrieving Data",
			onBeforeOpen: () => {
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					await this.executeCommissionQuery();
					Swal.close();
				} catch (err) {
					Swal.fire({
						icon: "error",
						title: "Unable to retrieve data",
						text: err,
						onClose: () => {},
					});
				}
			},
		});
	};

	addFilterData = (payload) => {
		const {
			merchants,
			selectedMerchant,
			viewingRange: { start, end },
			master,
		} = payload;

		const additionalData = {
			merchant:
				selectedMerchant === "all"
					? merchants.map((m) => m.value).filter((m) => m !== "all")
					: [selectedMerchant],
			startDate: start.startOf("day").toISOString(),
			endDate: end.endOf("day").toISOString(),
			isMaster: master,
		};
		return additionalData;
	};

	exportCurrent = (commissionList) => {
		const { selectedMerchant, merchants } = this.state;
		if (selectedMerchant && commissionList) {
			Swal.fire({
				title: "Exporting Commissions",
				onBeforeOpen: () => {
					Swal.showLoading();
				},
				onOpen: () => {
					try {
						const exportData = commissionList.map((e) => {
							return {
								...e,
								createdAt: Util.toDateTimeString(e.createdAt),
								...(e.currency && {
									currency: Util.currencyNumToCode(e.currency),
								}),

								// Handle invalid starting characters for excel/sheets
								transactionId: `="${e.transactionId}"`,
								mid: `="${e.mid}"`,
							};
						});

						// Prepare filename
						const targetMerchant = merchants.find(
							(merchant) => merchant.value === selectedMerchant
						);
						const merchantName = targetMerchant.name
							.replace(/[^a-z0-9]/gi, "_")
							.toLowerCase();
						const { start, end } = this.state.viewingRange;
						const startDate = start.toISOString().split("T")[0];
						const endDate = end.toISOString().split("T")[0];

						// Export commissions per payment gateway
						const paymentGatewayGrouped = groupBy(exportData, "paymentGateway");
						forEach(paymentGatewayGrouped, (commissions, paymentGateway) => {
							// Export grouped by currency
							const paymentGatewayCurrencyGrouped = groupBy(
								commissions,
								"currency"
							);
							forEach(
								paymentGatewayCurrencyGrouped,
								(commissions, currency) => {
									if (currency && currency !== "undefined") {
										const filename = `${merchantName}_commissions_${paymentGateway}_${currency}_${startDate}_${endDate}`;
										this.exportCsv(commissions, filename);
									}
								}
							);

							// Export commissions per reseller
							const resellerGrouped = groupBy(commissions, "reseller");
							forEach(resellerGrouped, (commissions, reseller) => {
								if (reseller && reseller !== "undefined") {
									const filename = `${merchantName}_commissions_${paymentGateway}_${reseller}_${startDate}_${endDate}`;
									this.exportCsv(commissions, filename);
								}
							});
						});
					} catch (err) {
						console.log(err);
						Swal.fire({
							icon: "error",
							title: "Failed to export",
						});
					} finally {
						Swal.close();
					}
				},
			});
		}
	};

	exportCsv = (data, filename) => {
		window.exportFromJSON({
			data,
			fileName: filename.replace(/_{2,}/g, "_"),
			exportType: "csv",
		});
	};

	exportReport = async (payload) => {
		Swal.fire({
			title: "Exporting Records",
			onBeforeOpen: () => {
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					const {
						merchants,
						selectedMerchant,
						viewingRange: { start, end },
						master,
					} = payload;

					const request = {
						merchant:
							selectedMerchant === "all"
								? merchants.map((m) => m.value).filter((m) => m !== "all")
								: [selectedMerchant],
						startDate: start.startOf("day").toISOString(),
						endDate: end.endOf("day").toISOString(),
						isMaster: master,
					};

					const { data } = await reportAxios.post("/api/report", request, {
						headers: {
							Authorization: `Bearer ${this.state.reportConfig.reportToken}`,
						},
						responseType: "blob",
					});

					let merchantName = "all_merchants";
					if (selectedMerchant !== "all") {
						const targetMerchant = merchants.find(
							(merchant) => merchant.value === selectedMerchant
						);
						merchantName = targetMerchant.name
							.replace(/[^a-z0-9]/gi, "_")
							.toLowerCase();
					}

					const filename = `${merchantName}_${start.format(
						"YYYY-MM-DD"
					)}_${end.format("YYYY-MM-DD")}.xlsx`;
					Util.downloadReport(data, filename);
					Swal.close();
				} catch (err) {
					Swal.fire({
						icon: "error",
						title: "Unable to export records",
						text: err,
						onClose: () => {},
					});
				}
			},
		});
	};

	renderSettlementsTable = () => {
		const { commissionSummary } = this.state;

		if (!commissionSummary || Object.keys(commissionSummary).length === 0)
			return <div className="uk-text-center">No Settlements Found</div>;

		const columns = [
			{
				data: "settlementDate",
				render: (data) => Util.toDateTimeString(data, true),
			},
			{
				data: "total",
				render: (data) => `$${data.toFixed(2)}`,
			},
			{
				data: "transactionFees",
				render: (data) => `$${data.toFixed(2)}`,
			},
			{
				data: "count",
			},
		];

		return Object.entries(commissionSummary).map(
			([paymentGateway, currencyGroup]) => {
				return Object.entries(currencyGroup).map(([currency, transactions]) => {
					return (
						<div key={currency}>
							<hr className="uk-divider-icon" />
							<h1 className="content-header">
								{Util.gateway[paymentGateway]} Settlements (
								{Util.currencyNumToCode(currency)})
							</h1>
							<DataTable
								className="uk-table uk-table-hover uk-table-striped uk-table-middle uk-table-responsive sales-channel-table uk-text-small"
								options={{
									data: transactions,
									columns,
									columnDefs: [
										{
											targets: [0],
											createdCell: (td, cellData) => {
												if (cellData) $(td).attr("uk-tooltip", cellData);
											},
										},
									],
									order: [[0, "desc"]],
								}}
							>
								<thead>
									<tr>
										<th>Settlement Date</th>
										<th>Total Amount</th>
										<th>Total Fees</th>
										<th>Count</th>
									</tr>
								</thead>
							</DataTable>
						</div>
					);
				});
			}
		);
	};

	render() {
		const { identifier, MDRTypes } = this.props;
		const {
			reportConfig,
			queryCounter,
			merchants,
			selectedMerchant,
			viewingRange: { start, end, maxDate },
			commissionListTotal,
			commissionListTransactions,
		} = this.state;

		return (
			<Container ref={(c) => (this.container = c)} activeMenu={identifier}>
				<div className="Merchants">
					<div className="uk-flex uk-flex-row uk-flex-middle uk-margin-bottom">
						<h1 className="content-header uk-margin-right uk-margin-remove-bottom">
							{identifier}
						</h1>
					</div>
					{merchants.length > 0 && (
						<div className="uk-card uk-card-small uk-card-body uk-card-default">
							<div className="uk-margin uk-flex">
								<SelectSearch
									options={merchants}
									search
									placeholder="Select Merchant"
									onChange={this.selectMerchant}
								/>
								{queryCounter > 0 && (
									<button
										className="uk-button uk-button-small uk-button-primary uk-margin-left action-btn"
										type="button"
										onClick={() => this.exportReport(this.state)}
									>
										<span className="uk-text-middle uk-text-bold">
											Export Records
										</span>
									</button>
								)}
							</div>
							{selectedMerchant && (
								<>
									<DateRange
										startDate={start && start.toDate()}
										endDate={end && end.toDate()}
										maxDate={maxDate.toDate()}
										dateChange={this.dateChange}
										search={this.search}
									/>
									{queryCounter > 0 && (
										<>
											<h1 className="content-header">Commissions</h1>
											<StatisticsGrid
												monthCommissionTotal={commissionListTotal}
												monthTransactionsTotal={commissionListTransactions}
											/>
											<DataTable
												ref={this.commissionsRef}
												className="uk-table uk-table-hover uk-table-striped uk-table-middle uk-table-responsive sales-channel-table uk-text-small"
												options={{
													serverSide: true,
													ajax: {
														url: `${reportConfig.endpoint}/api/commissions`,
														type: "POST",
														headers: {
															Authorization: `Bearer ${reportConfig.reportToken}`,
														},
														data: (payload) => {
															const additionalData = this.addFilterData(
																this.state
															);
															return { ...payload, ...additionalData };
														},
													},
													processing: true,
													destroy: true,
													columns: [
														{
															data: "transactionId",
															orderable: false,
															className: "uk-width-1-6 uk-text-truncate",
														},
														{
															data: "paymentType",
															className: "uk-text-center uk-text-truncate",
															render: (data, type) => {
																if (type === "display") {
																	if (data) {
																		return MDRTypes[data]
																			? `<img className="uk-img" src="data:image/*;base64,${MDRTypes[data].image}" width="35"/>`
																			: "Payment Link";
																	} else return "-";
																} else return data || "-";
															},
														},
														{
															data: "amount",
															className: "uk-text-center uk-text-truncate",
															render: (data) => `$${data}`,
														},
														{
															data: "transactionFees",
															className: "uk-text-center uk-text-truncate",
															render: (data) => `$${data}`,
														},
														{
															data: "commission",
															className: "uk-text-center uk-text-truncate",
															render: (data) =>
																`$${data ? data.toFixed(2) : 0}`,
														},
														{
															data: "createdAt",
															className: "uk-text-center uk-text-truncate",
															render: (data, type) => {
																return type === "sort"
																	? data
																	: Util.toDateTimeString(data);
															},
														},
													],
													order: [[5, "desc"]],
													columnDefs: [
														{
															targets: [0, 5],
															createdCell: (td, cellData) => {
																if (cellData)
																	$(td).attr("uk-tooltip", cellData);
															},
														},
													],
												}}
												// queryCounter={queryCounter}
											>
												<thead>
													<tr>
														<th>Transaction ID</th>
														<th>Payment Type</th>
														<th>Amount</th>
														<th>Transaction Fees</th>
														<th>Commission Amount</th>
														<th>Created At</th>
													</tr>
												</thead>
											</DataTable>
										</>
									)}
									{/* {this.renderCommissionsTable()}
									{this.renderSettlementsTable()} */}
									{this.renderSettlementsTable()}
								</>
							)}
						</div>
					)}
				</div>
			</Container>
		);
	}
}

export default withSSR(withRouter(Commissions));
